import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Check, X } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Progress } from '../../components/ui/Progress';
import { Slider } from '../../components/ui/Slider';
import { ScrollArea } from '../../components/ui/ScrollArea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import { youtubeVideos, youtubeCollections } from '../../src/seed';
export function YouTubeVideoDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const video = youtubeVideos.find(v => v.id === id);
  if (!video) {
    return <Card data-id="element-831">
        <CardHeader data-id="element-832">
          <CardTitle data-id="element-833">Video not found</CardTitle>
        </CardHeader>
      </Card>;
  }
  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const formatViews = (views: number) => {
    if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
    if (views >= 1000) return `${(views / 1000).toFixed(1)}K`;
    return views.toString();
  };
  const videoCollections = youtubeCollections.filter(c => video.collectionIds.includes(c.id));
  return <div className="space-y-6 pb-10" data-id="element-834">
      <div data-id="element-835">
        <Button variant="ghost" size="sm" className="-ml-3 mb-2 text-muted-foreground" onClick={() => navigate(-1)} data-id="element-836">
          <ArrowLeft className="mr-2 h-4 w-4" data-id="element-837" />
          Back
        </Button>
        <h1 className="text-3xl font-bold tracking-tight mb-3" data-id="element-838">
          {video.title}
        </h1>
        <div className="flex flex-wrap gap-2" data-id="element-839">
          {videoCollections.map(c => <Badge key={c.id} variant="outline" data-id="element-840">
              {c.name}
            </Badge>)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-841">
        <div className="lg:col-span-2 space-y-6" data-id="element-842">
          <Card className="overflow-hidden bg-black text-white aspect-video flex items-center justify-center relative" data-id="element-843">
            <Button variant="ghost" size="icon" className="h-20 w-20 rounded-full bg-white/10 hover:bg-white/20 text-white" onClick={() => setIsPlaying(!isPlaying)} data-id="element-844">
              <Play className="h-10 w-10 ml-2" data-id="element-845" />
            </Button>
          </Card>

          <Card data-id="element-846">
            <CardContent className="p-4 flex items-center gap-4" data-id="element-847">
              <Button size="icon" variant="ghost" className="rounded-full h-10 w-10 shrink-0" onClick={() => setIsPlaying(!isPlaying)} data-id="element-848">
                <Play className="h-5 w-5 ml-1" data-id="element-849" />
              </Button>
              <div className="flex-1 space-y-2" data-id="element-850">
                <Progress value={isPlaying ? 35 : 0} className="h-2" data-id="element-851" />
                <div className="flex justify-between text-xs text-muted-foreground font-medium" data-id="element-852">
                  <span data-id="element-853">{isPlaying ? '03:42' : '0:00'}</span>
                  <span data-id="element-854">{formatDuration(video.duration)}</span>
                </div>
              </div>
              <div className="w-24 shrink-0" data-id="element-855">
                <Slider defaultValue={[80]} max={100} step={1} data-id="element-856" />
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-857">
            <CardContent className="p-6" data-id="element-858">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-4 gap-x-8 text-sm" data-id="element-859">
                <div data-id="element-860">
                  <p className="text-muted-foreground mb-1" data-id="element-861">Channel</p>
                  <p className="font-medium" data-id="element-862">{video.channel}</p>
                </div>
                <div data-id="element-863">
                  <p className="text-muted-foreground mb-1" data-id="element-864">Duration</p>
                  <p className="font-medium" data-id="element-865">
                    {formatDuration(video.duration)}
                  </p>
                </div>
                <div data-id="element-866">
                  <p className="text-muted-foreground mb-1" data-id="element-867">Views</p>
                  <p className="font-medium" data-id="element-868">{formatViews(video.views)}</p>
                </div>
                <div data-id="element-869">
                  <p className="text-muted-foreground mb-1" data-id="element-870">Published</p>
                  <p className="font-medium" data-id="element-871">{formatDate(video.published)}</p>
                </div>
                <div data-id="element-872">
                  <p className="text-muted-foreground mb-1" data-id="element-873">License</p>
                  <p className="font-medium" data-id="element-874">Standard YouTube</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-875">
            <CardHeader className="pb-3" data-id="element-876">
              <CardTitle className="text-sm font-medium text-muted-foreground" data-id="element-877">
                Timeline Events
              </CardTitle>
            </CardHeader>
            <CardContent data-id="element-878">
              <div className="relative h-12 flex items-center" data-id="element-879">
                <div className="absolute left-0 right-0 h-1 bg-muted rounded-full" data-id="element-880" />
                <div className="absolute left-[10%] -mt-3 flex flex-col items-center cursor-pointer" data-id="element-881">
                  <div className="h-3 w-3 rounded-full bg-primary mb-1" data-id="element-882" />
                  <span className="text-[10px] font-medium" data-id="element-883">Q1</span>
                </div>
                <div className="absolute left-[25%] -mt-3 flex flex-col items-center cursor-pointer" data-id="element-884">
                  <div className="h-3 w-3 rounded-full bg-primary mb-1" data-id="element-885" />
                  <span className="text-[10px] font-medium" data-id="element-886">Q2</span>
                </div>
                <div className="absolute left-[45%] -mt-3 flex flex-col items-center cursor-pointer" data-id="element-887">
                  <div className="h-3 w-3 rounded-full bg-primary mb-1" data-id="element-888" />
                  <span className="text-[10px] font-medium" data-id="element-889">Q3</span>
                </div>
                <div className="absolute left-[60%] -mt-3 flex flex-col items-center cursor-pointer" data-id="element-890">
                  <div className="h-3 w-3 rounded-full bg-primary mb-1" data-id="element-891" />
                  <span className="text-[10px] font-medium" data-id="element-892">Q4</span>
                </div>
                <div className="absolute left-[85%] -mt-3 flex flex-col items-center cursor-pointer" data-id="element-893">
                  <div className="h-3 w-3 rounded-full bg-primary mb-1" data-id="element-894" />
                  <span className="text-[10px] font-medium" data-id="element-895">Q5</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6" data-id="element-896">
          <Card className="h-full flex flex-col" data-id="element-897">
            <CardHeader data-id="element-898">
              <CardTitle data-id="element-899">Transcript</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 p-0" data-id="element-900">
              <ScrollArea className="h-[600px] px-6 pb-6" data-id="element-901">
                <div className="space-y-4" data-id="element-902">
                  <div className="flex gap-3" data-id="element-903">
                    <span className="text-xs text-muted-foreground font-mono w-12 shrink-0 pt-0.5" data-id="element-904">
                      0:00
                    </span>
                    <p className="text-sm leading-relaxed" data-id="element-905">
                      Welcome back everyone. Today we're going to dive deep into
                      the cardiac cycle, breaking down the complex sequence of
                      events that occurs with every single heartbeat.
                    </p>
                  </div>
                  <div className="flex gap-3" data-id="element-906">
                    <span className="text-xs text-muted-foreground font-mono w-12 shrink-0 pt-0.5" data-id="element-907">
                      1:15
                    </span>
                    <p className="text-sm leading-relaxed" data-id="element-908">
                      Let's start with diastole. During this phase, the heart
                      muscle is relaxed. Blood flows passively from the atria
                      into the ventricles through the open AV valves - that's
                      the tricuspid and mitral valves.
                    </p>
                  </div>
                  <div className="flex gap-3 bg-accent/50 p-2 -mx-2 rounded-md" data-id="element-909">
                    <span className="text-xs text-accent-foreground/70 font-mono w-12 shrink-0 pt-0.5" data-id="element-910">
                      2:30
                    </span>
                    <p className="text-sm leading-relaxed font-medium" data-id="element-911">
                      Now, atrial systole occurs. The atria contract, pushing
                      that final 20% of blood volume into the ventricles. This
                      is often called the "atrial kick" and it's crucial for
                      maximizing ventricular filling.
                    </p>
                  </div>
                  <div className="flex gap-3" data-id="element-912">
                    <span className="text-xs text-muted-foreground font-mono w-12 shrink-0 pt-0.5" data-id="element-913">
                      3:45
                    </span>
                    <p className="text-sm leading-relaxed" data-id="element-914">
                      As ventricular pressure rises above atrial pressure, the
                      AV valves snap shut. This closure creates the first heart
                      sound, S1, or the "lub" in the "lub-dub" sequence.
                    </p>
                  </div>
                  <div className="flex gap-3" data-id="element-915">
                    <span className="text-xs text-muted-foreground font-mono w-12 shrink-0 pt-0.5" data-id="element-916">
                      4:20
                    </span>
                    <p className="text-sm leading-relaxed" data-id="element-917">
                      For a brief moment, all four valves are closed while the
                      ventricles are contracting. This is called isovolumetric
                      contraction. The pressure is building rapidly, but the
                      volume stays exactly the same.
                    </p>
                  </div>
                  <div className="flex gap-3" data-id="element-918">
                    <span className="text-xs text-muted-foreground font-mono w-12 shrink-0 pt-0.5" data-id="element-919">
                      5:10
                    </span>
                    <p className="text-sm leading-relaxed" data-id="element-920">
                      Once ventricular pressure exceeds the pressure in the
                      aorta and pulmonary artery, the semilunar valves are
                      forced open. This marks the beginning of ventricular
                      ejection.
                    </p>
                  </div>
                  <div className="flex gap-3" data-id="element-921">
                    <span className="text-xs text-muted-foreground font-mono w-12 shrink-0 pt-0.5" data-id="element-922">
                      6:30
                    </span>
                    <p className="text-sm leading-relaxed" data-id="element-923">
                      As the ventricles begin to relax, their pressure drops
                      below that of the great vessels. Blood tries to flow
                      backward, catching in the cusps of the semilunar valves
                      and snapping them shut.
                    </p>
                  </div>
                  <div className="flex gap-3" data-id="element-924">
                    <span className="text-xs text-muted-foreground font-mono w-12 shrink-0 pt-0.5" data-id="element-925">
                      7:15
                    </span>
                    <p className="text-sm leading-relaxed" data-id="element-926">
                      This closure produces the second heart sound, S2, or the
                      "dub". We are now in isovolumetric relaxation, where
                      again, all four valves are closed.
                    </p>
                  </div>
                  <div className="flex gap-3" data-id="element-927">
                    <span className="text-xs text-muted-foreground font-mono w-12 shrink-0 pt-0.5" data-id="element-928">
                      8:00
                    </span>
                    <p className="text-sm leading-relaxed" data-id="element-929">
                      Finally, when ventricular pressure drops below atrial
                      pressure, the AV valves open again, and the cycle starts
                      all over. A beautiful, continuous, life-sustaining rhythm.
                    </p>
                  </div>
                  <div className="flex gap-3" data-id="element-930">
                    <span className="text-xs text-muted-foreground font-mono w-12 shrink-0 pt-0.5" data-id="element-931">
                      9:20
                    </span>
                    <p className="text-sm leading-relaxed" data-id="element-932">
                      Next time, we'll look at how this mechanical cycle
                      correlates with the electrical activity seen on an ECG.
                      Thanks for watching, and stay curious.
                    </p>
                  </div>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card className="w-full" data-id="element-933">
        <CardHeader data-id="element-934">
          <CardTitle data-id="element-935">Tools</CardTitle>
        </CardHeader>
        <CardContent data-id="element-936">
          <Tabs defaultValue="questions" data-id="element-937">
            <TabsList data-id="element-938">
              <TabsTrigger value="transcribe" data-id="element-939">Transcribe</TabsTrigger>
              <TabsTrigger value="questions" data-id="element-940">Generate questions</TabsTrigger>
            </TabsList>
            <div className="mt-6" data-id="element-941">
              <TabsContent value="transcribe" data-id="element-942">
                <div className="flex items-center gap-3" data-id="element-943">
                  <Check className="h-5 w-5 text-green-500" data-id="element-944" />
                  <div data-id="element-945">
                    <p className="font-medium" data-id="element-946">Transcribed yesterday</p>
                    <p className="text-sm text-muted-foreground" data-id="element-947">
                      48 min · 9,000 words
                    </p>
                  </div>
                </div>
                <Button variant="ghost" className="mt-4" data-id="element-948">
                  Re-transcribe
                </Button>
              </TabsContent>
              <TabsContent value="questions" data-id="element-949">
                <div className="space-y-6" data-id="element-950">
                  <div className="space-y-4" data-id="element-951">
                    {/* Question 1: True/False */}
                    <div className="border rounded-lg p-4 bg-card" data-id="element-952">
                      <div className="flex items-start justify-between mb-3" data-id="element-953">
                        <div className="flex items-center gap-2" data-id="element-954">
                          <Badge variant="outline" className="text-xs font-normal" data-id="element-955">
                            True / False
                          </Badge>
                          <Badge variant="secondary" className="text-xs font-mono cursor-pointer hover:bg-secondary/80" data-id="element-956">
                            2:15 →
                          </Badge>
                        </div>
                        <div className="flex gap-1" data-id="element-957">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50" data-id="element-958">
                            <Check className="h-4 w-4" data-id="element-959" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" data-id="element-960">
                            <X className="h-4 w-4" data-id="element-961" />
                          </Button>
                        </div>
                      </div>
                      <p className="font-medium text-sm mb-3" data-id="element-962">
                        The "atrial kick" contributes approximately 50% of the
                        total ventricular filling volume.
                      </p>
                      <div className="space-y-2 text-sm" data-id="element-963">
                        <div className="flex items-center gap-2 p-2 rounded border border-transparent" data-id="element-964">
                          <div className="h-4 w-4 rounded-full border border-primary/50" data-id="element-965" />
                          <span data-id="element-966">True</span>
                        </div>
                        <div className="flex items-center gap-2 p-2 rounded bg-green-50 border border-green-200 text-green-900" data-id="element-967">
                          <div className="h-4 w-4 rounded-full bg-green-600 flex items-center justify-center" data-id="element-968">
                            <div className="h-1.5 w-1.5 rounded-full bg-white" data-id="element-969" />
                          </div>
                          <span className="font-medium" data-id="element-970">False</span>
                        </div>
                      </div>
                    </div>

                    {/* Question 2: Multiple Choice */}
                    <div className="border rounded-lg p-4 bg-card" data-id="element-971">
                      <div className="flex items-start justify-between mb-3" data-id="element-972">
                        <div className="flex items-center gap-2" data-id="element-973">
                          <Badge variant="outline" className="text-xs font-normal" data-id="element-974">
                            Multiple Choice
                          </Badge>
                          <Badge variant="secondary" className="text-xs font-mono cursor-pointer hover:bg-secondary/80" data-id="element-975">
                            4:20 →
                          </Badge>
                        </div>
                        <div className="flex gap-1" data-id="element-976">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50" data-id="element-977">
                            <Check className="h-4 w-4" data-id="element-978" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" data-id="element-979">
                            <X className="h-4 w-4" data-id="element-980" />
                          </Button>
                        </div>
                      </div>
                      <p className="font-medium text-sm mb-3" data-id="element-981">
                        What characterizes the isovolumetric contraction phase?
                      </p>
                      <div className="space-y-2 text-sm" data-id="element-982">
                        <div className="p-2 rounded border border-muted" data-id="element-983">
                          A. AV valves are open, semilunar valves are closed
                        </div>
                        <div className="p-2 rounded border border-muted" data-id="element-984">
                          B. All four valves are open
                        </div>
                        <div className="p-2 rounded bg-green-50 border border-green-200 text-green-900 font-medium" data-id="element-985">
                          C. All four valves are closed while ventricular
                          pressure rises
                        </div>
                        <div className="p-2 rounded border border-muted" data-id="element-986">
                          D. Semilunar valves are open, AV valves are closed
                        </div>
                      </div>
                    </div>

                    {/* Question 3: Matching */}
                    <div className="border rounded-lg p-4 bg-card" data-id="element-987">
                      <div className="flex items-start justify-between mb-3" data-id="element-988">
                        <div className="flex items-center gap-2" data-id="element-989">
                          <Badge variant="outline" className="text-xs font-normal" data-id="element-990">
                            Matching
                          </Badge>
                          <Badge variant="secondary" className="text-xs font-mono cursor-pointer hover:bg-secondary/80" data-id="element-991">
                            7:15 →
                          </Badge>
                        </div>
                        <div className="flex gap-1" data-id="element-992">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50" data-id="element-993">
                            <Check className="h-4 w-4" data-id="element-994" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" data-id="element-995">
                            <X className="h-4 w-4" data-id="element-996" />
                          </Button>
                        </div>
                      </div>
                      <p className="font-medium text-sm mb-3" data-id="element-997">
                        Match the heart sound to its physiological cause:
                      </p>
                      <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-998">
                        <div className="space-y-2" data-id="element-999">
                          <div className="p-2 bg-muted/50 rounded text-center" data-id="element-1000">
                            S1 ("lub")
                          </div>
                          <div className="p-2 bg-muted/50 rounded text-center" data-id="element-1001">
                            S2 ("dub")
                          </div>
                        </div>
                        <div className="space-y-2" data-id="element-1002">
                          <div className="p-2 border border-muted rounded" data-id="element-1003">
                            Closure of AV valves
                          </div>
                          <div className="p-2 border border-muted rounded" data-id="element-1004">
                            Closure of semilunar valves
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pt-4 border-t" data-id="element-1005">
                    <Button data-id="element-1006">Accept all</Button>
                    <Button variant="outline" data-id="element-1007">Regenerate all</Button>
                    <Button variant="destructive" className="ml-auto" data-id="element-1008">
                      Reject all
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </CardContent>
      </Card>
    </div>;
}